/* assets/mss-mug.js — sections/mss-mug-main.liquid (MSS mug product, .co Shrine PRO buy box rebuilt for .com).
 *
 * Custom elements (each guarded by customElements.get, all clean up on disconnect so theme-editor re-renders work):
 *   <mss-mug-main>     section controller: form submit + sticky button -> one addToCart(), accordion aria-expanded
 *   <mss-mug-gallery>  slider + thumbnails + muted looping video; goToMediaId(id) slides ONLY the slider
 *   <mss-mug-bundle>   .co quantity breaks: tiers, per-unit character pickers, getItems() with Shrine semantics,
 *                      getState(), emits 'mss-mug:change' {section, qty, priceHtml, compareHtml, onSale, savePct}
 *   <mss-mug-dates>    shipping checkpoints, calendar days in the shopper's local time ("Oct 20th")
 *   <mss-mug-sticky>   .co sticky add-to-cart bar (after scroll, phone + desktop)
 *
 * Add to cart: POST {routes.cart_add_url}.js {items, sections, sections_url}; cart-drawer.renderContents(data);
 * publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items', origin: 'mss-mug', cartData}) so cart-drawer-items
 * does not fetch the drawer twice. product-form.js / product-info.js are not used.
 */
(() => {
  'use strict';

  /* ---------------- helpers ---------------- */

  function withDelimiters(cents, precision, thousands, decimal) {
    const value = Number(cents);
    if (!isFinite(value)) return '0';
    const parts = (value / 100).toFixed(precision).split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
  }

  // Same token rules as assets/mss-pick.js formatWith(); hideZeros mirrors Shrine's money_without_trailing_zeros.
  function formatMoney(cents, format, hideZeros) {
    const fmt = String(format || (window.KvTheme && window.KvTheme.moneyFormat) || '${{amount}}').replace(/<[^>]*>/g, '');
    const token = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(token);
    let value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_apostrophe_separator': value = withDelimiters(cents, 0, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ','); break;
      default: value = withDelimiters(cents, 2, ',', '.');
    }
    if (hideZeros) value = value.replace(/([.,])00$/, '');
    return fmt.replace(token, value);
  }

  function routes() {
    const r = window.routes || {};
    return { add: `${r.cart_add_url || '/cart/add'}.js`, cart: r.cart_url || '/cart' };
  }

  function gallery(section) {
    return document.getElementById(`MssMugGallery-${section}`);
  }

  function slideGallery(section, mediaId) {
    const g = gallery(section);
    if (g && mediaId && typeof g.goToMediaId === 'function') g.goToMediaId(mediaId);
  }

  function formIdInput(section) {
    const form = document.getElementById(`product-form-${section}`);
    return form ? form.querySelector('[name="id"]') : null;
  }

  // Shopify StandardEvents analytics for the add (copied from kv-builder.js / mss-pick.js). Never throws.
  function createLinesEvent(items, target) {
    try {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent || typeof CartLinesUpdateEvent.createPromise !== 'function') return null;
      const deferred = CartLinesUpdateEvent.createPromise();
      // A refused add rejects this promise; Shopify's event wraps it in a promise of its own (event.promise).
      // Catch both so a refused add never reaches the page's error trackers as an uncaught rejection.
      const silence = (promise) => {
        if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      };
      silence(deferred && deferred.promise);
      const event = new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      });
      silence(event.promise);
      (target || document).dispatchEvent(event);
      return deferred;
    } catch (e) {
      return null;
    }
  }

  function settleLinesEvent(deferred, error) {
    if (!deferred) return;
    try {
      if (error) {
        deferred.reject(error);
        return;
      }
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      fetch(`${routes().cart}.json`)
        .then((response) => response.json())
        .then((cart) => {
          if (!cart || !cart.currency) throw new Error('Missing currency in cart response');
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        })
        .catch((e) => deferred.reject(e));
    } catch (e) {
      /* analytics only */
    }
  }

  /* ---------------- add to cart (main button AND sticky button) ---------------- */

  const busy = {};

  function addButtons(section) {
    const list = [];
    const main = document.getElementById(`ProductSubmitButton-${section}`);
    if (main) list.push(main);
    const sticky = document.getElementById(`MssMugSticky-${section}`);
    if (sticky) sticky.querySelectorAll('[data-mss-mug-add]').forEach((b) => list.push(b));
    return list;
  }

  function showError(section, message, trigger) {
    const root = document.getElementById(`MainProduct-${section}`);
    const wrap = root && root.querySelector('[data-mss-mug-error]');
    if (!wrap) {
      if (message) window.alert(message);
      return;
    }
    const msg = wrap.querySelector('[data-mss-mug-error-msg]');
    if (!message) {
      wrap.hidden = true;
      if (msg) msg.textContent = '';
      return;
    }
    if (msg) msg.textContent = message;
    wrap.hidden = false;
    if (trigger && trigger.closest('mss-mug-sticky')) {
      const r = wrap.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) wrap.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  function setBusy(section, trigger, on) {
    busy[section] = on;
    addButtons(section).forEach((b) => {
      if (on) b.setAttribute('aria-disabled', 'true');
      else b.removeAttribute('aria-disabled');
    });
    if (trigger) {
      trigger.classList.toggle('is-loading', on);
      trigger.setAttribute('aria-busy', on ? 'true' : 'false');
    }
  }

  function addToCart(section, trigger) {
    if (busy[section]) return Promise.resolve(null);
    if (trigger && (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true')) return Promise.resolve(null);

    const bundle = document.getElementById(`quantity-breaks-${section}`);
    let items = bundle && typeof bundle.getItems === 'function' ? bundle.getItems() : null;
    if (!items || !items.length) {
      const input = formIdInput(section);
      items = input ? [{ id: Number(input.value), quantity: 1 }] : [];
    }
    items = items.filter((item) => item.id && item.quantity > 0);
    if (!items.length) return Promise.resolve(null);

    const drawer = document.querySelector('cart-drawer');
    const canDrawer =
      drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function';
    const sections = canDrawer ? drawer.getSectionsToRender().map((s) => s.id) : [];
    if (sections.indexOf('mss-cart-state') === -1) sections.push('mss-cart-state');
    if (canDrawer && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger || document.activeElement);

    showError(section, '');
    setBusy(section, trigger, true);
    const root = document.getElementById(`MainProduct-${section}`);
    const linesEvent = createLinesEvent(items, root);

    return fetch(routes().add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items, sections: sections.slice(0, 5), sections_url: window.location.pathname }),
    })
      .then((response) => response.json().catch(() => ({})).then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || (data && data.status)) {
          const message =
            (data && (data.description || data.message)) || (window.cartStrings && window.cartStrings.error) || 'Error';
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'mss-mug',
              productVariantId: items[0].id,
              errors: data && (data.errors || data.description),
              message: data && data.message,
            });
          }
          settleLinesEvent(linesEvent, new Error(message));
          showError(section, message, trigger);
          return null;
        }
        settleLinesEvent(linesEvent);
        if (canDrawer && data && data.sections) {
          try {
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } catch (error) {
            console.error('[mss-mug] drawer render failed', error);
            window.location = routes().cart;
            return data;
          }
        } else {
          window.location = routes().cart;
          return data;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            origin: 'mss-mug',
            productVariantId: items[0].id,
            cartData: data,
          });
        }
        document.dispatchEvent(new CustomEvent('mss:cart-added', { detail: { items, source: 'mss-mug' } }));
        return data;
      })
      .catch((error) => {
        console.error('[mss-mug]', error);
        settleLinesEvent(linesEvent, error);
        showError(section, (window.cartStrings && window.cartStrings.error) || 'Error', trigger);
        return null;
      })
      .finally(() => setBusy(section, trigger, false));
  }

  window.MssMug = Object.assign(window.MssMug || {}, { addToCart, formatMoney });

  /* ---------------- <mss-mug-main> ---------------- */

  if (!customElements.get('mss-mug-main')) {
    class MssMugMain extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
        this.form = document.getElementById(`product-form-${this.section}`);
        this.onSubmit = (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          addToCart(this.section, event.submitter || document.getElementById(`ProductSubmitButton-${this.section}`));
        };
        this.onClick = (event) => {
          const btn = event.target.closest('[data-mss-mug-add]');
          if (!btn || !this.contains(btn) || btn.type === 'submit') return;
          event.preventDefault();
          addToCart(this.section, btn);
        };
        this.onToggle = (event) => {
          const details = event.target;
          if (!details || !details.classList || !details.classList.contains('mss-mug-acc__details')) return;
          const summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        };
        if (this.form) this.form.addEventListener('submit', this.onSubmit, true);
        this.addEventListener('click', this.onClick);
        this.addEventListener('toggle', this.onToggle, true);
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this.onSubmit, true);
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('toggle', this.onToggle, true);
      }
    }
    customElements.define('mss-mug-main', MssMugMain);
  }

  /* ---------------- <mss-mug-gallery> ---------------- */

  if (!customElements.get('mss-mug-gallery')) {
    class MssMugGallery extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
        this.list = this.querySelector('[data-gallery-list]');
        if (!this.list) return;
        this.slides = Array.from(this.list.children);
        this.thumbList = this.querySelector('[data-thumb-list]');
        this.thumbItems = this.thumbList ? Array.from(this.thumbList.children) : [];
        this.prevBtn = this.querySelector('[data-thumb-prev]');
        this.nextBtn = this.querySelector('[data-thumb-next]');
        const main = this.closest('mss-mug-main');
        this.autoplay = !main || main.dataset.videoAutoplay !== 'false';
        this.activeIndex = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));

        this.onThumbClick = (event) => {
          const btn = event.target.closest('.mss-mug-gallery__thumb');
          if (!btn) return;
          const li = btn.closest('.mss-mug-gallery__thumb-item');
          const index = this.thumbItems.indexOf(li);
          if (index > -1) this.goToIndex(index);
        };
        this.onPrev = () => this.stepThumbs(-1);
        this.onNext = () => this.stepThumbs(1);
        this.onThumbScroll = () => this.queue('arrows', () => this.updateArrows());
        this.onMainScroll = () => {
          window.clearTimeout(this.mainTimer);
          this.mainTimer = window.setTimeout(() => this.onMainScrollEnd(), 90);
        };
        this.onResize = () => this.queue('resize', () => this.updateArrows());
        this.onVideoClick = (event) => {
          const btn = event.target.closest('.mss-mug-video__play');
          if (!btn) return;
          event.preventDefault();
          this.toggleVideo(btn.closest('.mss-mug-video'));
        };

        if (this.thumbList) {
          this.thumbList.addEventListener('click', this.onThumbClick);
          this.thumbList.addEventListener('scroll', this.onThumbScroll, { passive: true });
        }
        if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
        this.list.addEventListener('scroll', this.onMainScroll, { passive: true });
        this.list.addEventListener('click', this.onVideoClick);
        window.addEventListener('resize', this.onResize);

        this.videos = Array.from(this.querySelectorAll('[data-video]'));
        // the overlay button's accessible name follows the real playback state (it pauses a playing video)
        this.videos.forEach((wrap) => {
          const video = wrap.querySelector('video');
          const btn = wrap.querySelector('.mss-mug-video__play');
          if (!video || !btn) return;
          const sync = () => {
            const label = video.paused ? btn.dataset.labelPlay : btn.dataset.labelPause;
            if (label) btn.setAttribute('aria-label', label);
          };
          video.addEventListener('play', sync);
          video.addEventListener('pause', sync);
          sync();
        });
        // Any intersection counts as on screen: the active video keeps playing while part of it shows and only
        // pauses once it is fully off screen (it resumes when it comes back).
        if (this.videos.length && 'IntersectionObserver' in window) {
          this.io = new IntersectionObserver(
            (entries) => entries.forEach((entry) => this.onVideoVisibility(entry)),
            { threshold: [0] }
          );
          this.videos.forEach((v) => this.io.observe(v));
        }
        this.updateArrows();
      }

      disconnectedCallback() {
        if (!this.list) return;
        if (this.thumbList) {
          this.thumbList.removeEventListener('click', this.onThumbClick);
          this.thumbList.removeEventListener('scroll', this.onThumbScroll);
        }
        if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
        this.list.removeEventListener('scroll', this.onMainScroll);
        this.list.removeEventListener('click', this.onVideoClick);
        window.removeEventListener('resize', this.onResize);
        if (this.io) this.io.disconnect();
        window.clearTimeout(this.mainTimer);
      }

      queue(key, fn) {
        this.frames = this.frames || {};
        if (this.frames[key]) return;
        this.frames[key] = window.requestAnimationFrame(() => {
          this.frames[key] = null;
          fn();
        });
      }

      // Public API used by the bundle pickers and the sticky dropdown: slides the gallery only, never the window.
      goToMediaId(mediaId) {
        const index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (index > -1) this.goToIndex(index);
      }

      goToIndex(index) {
        const slide = this.slides[index];
        if (!slide) return;
        this.setActive(index);
        this.list.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }

      slideStride() {
        if (this.slides.length < 2) return this.list.clientWidth || 1;
        return this.slides[1].offsetLeft - this.slides[0].offsetLeft || this.list.clientWidth || 1;
      }

      onMainScrollEnd() {
        const index = Math.round(this.list.scrollLeft / this.slideStride());
        if (index !== this.activeIndex && this.slides[index]) this.setActive(index);
      }

      setActive(index) {
        this.activeIndex = index;
        this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
        this.thumbItems.forEach((li, i) => {
          const btn = li.querySelector('.mss-mug-gallery__thumb');
          if (!btn) return;
          if (i === index) btn.setAttribute('aria-current', 'true');
          else btn.removeAttribute('aria-current');
        });
        const thumb = this.thumbItems[index];
        if (thumb && this.thumbList && !this.isThumbVisible(thumb, 10)) {
          this.thumbList.scrollTo({ left: thumb.offsetLeft, behavior: 'smooth' });
        }
        this.syncVideos();
      }

      isActiveVideo(wrap) {
        const slide = wrap.closest('.mss-mug-gallery__slide');
        return !!slide && slide === this.slides[this.activeIndex];
      }

      // .co (Dawn playActiveMedia): a video loads and plays as soon as its slide becomes the active one (thumbnail,
      // swipe, character pick), however little of the gallery is on screen; every other video pauses.
      syncVideos() {
        (this.videos || []).forEach((wrap) => {
          if (!this.isActiveVideo(wrap)) this.suspendVideo(wrap);
          else if (this.autoplay && wrap.dataset.userPaused !== 'true') this.playVideo(wrap);
        });
      }

      // Dawn SliderComponent.isSlideVisible()
      isThumbVisible(el, offset = 0) {
        const last = this.thumbList.clientWidth + this.thumbList.scrollLeft - offset;
        return el.offsetLeft + el.clientWidth <= last && el.offsetLeft >= this.thumbList.scrollLeft;
      }

      stepThumbs(direction) {
        if (!this.thumbList || this.thumbItems.length < 2) return;
        const step = parseInt((this.nextBtn && this.nextBtn.dataset.step) || '3', 10);
        const offset = this.thumbItems[1].offsetLeft - this.thumbItems[0].offsetLeft;
        this.thumbList.scrollTo({ left: this.thumbList.scrollLeft + direction * step * offset, behavior: 'smooth' });
      }

      updateArrows() {
        if (!this.thumbList || !this.thumbItems.length) return;
        const first = this.thumbItems[0];
        const last = this.thumbItems[this.thumbItems.length - 1];
        if (this.prevBtn) {
          if (this.isThumbVisible(first) && this.thumbList.scrollLeft <= 0) this.prevBtn.setAttribute('disabled', 'disabled');
          else this.prevBtn.removeAttribute('disabled');
        }
        if (this.nextBtn) {
          if (this.isThumbVisible(last)) this.nextBtn.setAttribute('disabled', 'disabled');
          else this.nextBtn.removeAttribute('disabled');
        }
      }

      /* video: muted + looped, plays while its slide is the active one (and any of it is on screen),
         a tap pauses / resumes */
      loadVideo(wrap) {
        const video = wrap.querySelector('video');
        if (!video || video.dataset.loaded) return video;
        video.querySelectorAll('source[data-src]').forEach((s) => {
          s.src = s.dataset.src;
          s.removeAttribute('data-src');
        });
        video.dataset.loaded = 'true';
        video.muted = true;
        video.loop = true;
        video.load();
        return video;
      }

      playVideo(wrap) {
        const video = this.loadVideo(wrap);
        if (!video) return;
        wrap.classList.add('is-playing');
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => wrap.classList.remove('is-playing'));
      }

      pauseVideo(wrap) {
        const video = wrap.querySelector('video');
        if (video && !video.paused) video.pause();
        wrap.classList.remove('is-playing');
      }

      toggleVideo(wrap) {
        if (!wrap) return;
        const video = wrap.querySelector('video');
        if (video && video.dataset.loaded && !video.paused) {
          wrap.dataset.userPaused = 'true';
          this.pauseVideo(wrap);
        } else {
          delete wrap.dataset.userPaused;
          this.playVideo(wrap);
        }
      }

      // Pause a video that is not on show without revealing the play button. A shopper's own pause is kept (as on .co).
      suspendVideo(wrap) {
        const video = wrap.querySelector('video');
        if (video && video.dataset.loaded && !video.paused) video.pause();
        if (this.autoplay && wrap.dataset.userPaused !== 'true') wrap.classList.add('is-playing');
      }

      onVideoVisibility(entry) {
        const wrap = entry.target;
        if (!entry.isIntersecting) this.suspendVideo(wrap);
        else if (this.isActiveVideo(wrap) && this.autoplay && wrap.dataset.userPaused !== 'true') this.playVideo(wrap);
      }
    }
    customElements.define('mss-mug-gallery', MssMugGallery);
  }

  /* ---------------- <mss-mug-bundle> ---------------- */

  if (!customElements.get('mss-mug-bundle')) {
    class MssMugBundle extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
        this.inputs = Array.from(this.querySelectorAll('input.quantity-break__radio'));
        this.labels = Array.from(this.querySelectorAll('.quantity-break'));
        if (!this.inputs.length) return;
        const json = this.querySelector('script[data-mss-mug-variants]');
        try {
          this.variants = json ? JSON.parse(json.textContent) : [];
        } catch (e) {
          this.variants = [];
        }
        this.moneyFormat = this.dataset.moneyFormat;
        this.priceVaries = this.dataset.priceVaries === 'true';
        this.updatePrices = this.dataset.updatePrices === 'true';
        this.scrollGallery = this.dataset.scrollGallery !== 'false';
        // Shrine semantics: formVariants stays [] (untouched) until a tier is clicked or a picker changes.
        this.touched = false;
        this.selectedVariants = {};
        this.offerState = {};
        this.labels.forEach((label) => {
          const key = label.dataset.input;
          this.selectedVariants[key] = Array.from(label.querySelectorAll('.quantity-break__selector-item')).map(
            (row) => String(row.dataset.selectedId)
          );
          this.offerState[key] = {
            priceHtml: label.dataset.priceHtml,
            compareHtml: label.dataset.compareHtml,
            price: parseFloat(label.dataset.priceCents),
            compare: parseFloat(label.dataset.compareCents),
            savePct: parseInt(label.dataset.savePct, 10) || 0,
          };
        });
        const checked = this.inputs.find((i) => i.checked) || this.inputs[0];
        if (!checked.checked) checked.checked = true;
        this.selectedInput = checked.dataset.input;
        this.selectedQuantity = parseInt(checked.value, 10) || 1;

        this.onTierChange = (event) => {
          const input = event.target;
          if (!input.checked) return;
          this.touched = true;
          this.selectedInput = input.dataset.input;
          this.selectedQuantity = parseInt(input.value, 10) || 1;
          this.emit();
        };
        this.onPickerChange = (event) => this.handlePicker(event.currentTarget);
        this.inputs.forEach((i) => i.addEventListener('change', this.onTierChange));
        this.rows = Array.from(this.querySelectorAll('.quantity-break__selector-item'));
        this.rows.forEach((row) => row.addEventListener('change', this.onPickerChange));

        // Halloween Buy 3 Get 2 tier (snippets/mss-mug-bundle, data-mss-deal-tier): removed at the end moment
        // (window.MssOffer 'mss:offer-change', or the kv-deals sweep), and at once on a page cached past the end.
        this.onDealEnd = () => this.retireDealTiers();
        document.addEventListener('mss:offer-change', this.onDealEnd);
        document.addEventListener('kv:deal-expired', this.onDealEnd);
        if (!(window.MssOffer && window.MssOffer.live())) this.retireDealTiers(true);
        this.emit();
      }

      disconnectedCallback() {
        document.removeEventListener('mss:offer-change', this.onDealEnd);
        document.removeEventListener('kv:deal-expired', this.onDealEnd);
        if (!this.inputs || !this.inputs.length) return;
        this.inputs.forEach((i) => i.removeEventListener('change', this.onTierChange));
        (this.rows || []).forEach((row) => row.removeEventListener('change', this.onPickerChange));
      }

      // Remove the Halloween-only tier(s) once the deal is over. If one was selected, the selection moves to option 2
      // (else the first tier left), the sticky bar follows (emit), and the form can never post its quantity again.
      retireDealTiers(quiet) {
        if (window.MssOffer && window.MssOffer.live()) return;
        const dead = this.inputs.filter((i) => i.hasAttribute('data-mss-deal-tier'));
        if (!dead.length) return;
        let lost = false;
        dead.forEach((input) => {
          if (input.checked) lost = true;
          input.removeEventListener('change', this.onTierChange);
          const label = this.querySelector(`label[for="${input.id}"]`);
          if (label) label.remove();
          input.remove();
        });
        this.inputs = this.inputs.filter((i) => dead.indexOf(i) === -1);
        this.labels = Array.from(this.querySelectorAll('.quantity-break'));
        this.rows = Array.from(this.querySelectorAll('.quantity-break__selector-item'));
        if (!this.inputs.length) return;
        if (lost) {
          const next = this.inputs.find((i) => i.dataset.input === 'input_2') || this.inputs[0];
          next.checked = true;
          this.selectedInput = next.dataset.input;
          this.selectedQuantity = parseInt(next.value, 10) || 1;
        }
        if (!quiet || lost) this.emit();
      }

      handlePicker(row) {
        const values = Array.from(row.querySelectorAll('select'), (s) => s.value);
        const variant = this.variants.find((v) => v.options.every((opt, i) => values[i] === opt));
        if (!variant || !variant.available) return;
        row.dataset.selectedId = String(variant.id);
        const label = row.closest('.quantity-break');
        const key = label.dataset.input;
        this.selectedVariants[key][parseInt(row.dataset.selectIndex, 10)] = String(variant.id);
        this.touched = true;

        if (this.scrollGallery && variant.featured_media) slideGallery(this.section, variant.featured_media.id);
        const img = row.querySelector('.quantity-break__selector-item__image img');
        if (img && variant.featured_image) {
          const src = variant.featured_image.src || variant.featured_image;
          img.src = `${src}${String(src).indexOf('?') > -1 ? '&' : '?'}width=600`;
          if (variant.featured_image.alt) img.alt = variant.featured_image.alt;
        }
        // Prices are pre-rendered in Liquid. They only need recomputing when the variants are not all one price.
        if (this.updatePrices && this.priceVaries) this.recompute(label, key);
        this.emit();
      }

      // Same maths as the bundle snippet. With free units the cheapest units are free (the Buy X Get Y discount
      // checkout applies), so the tier price is the paid units' prices exactly; otherwise price x (1 - percentage).
      recompute(label, key) {
        const qty = parseInt(label.dataset.quantity, 10);
        const left = parseFloat(label.dataset.percentageLeft);
        const fixed = parseFloat(label.dataset.fixedDiscount) || 0;
        let free = Math.min(qty, Math.max(0, parseInt(label.dataset.freeUnits, 10) || 0));
        // Never more than the shared offer rule gives for this quantity (Buy 2 Get 1, + Buy 3 Get 2 while live).
        if (window.MssOffer) free = Math.min(free, window.MssOffer.freeUnits(qty));
        const useCompareAt = label.dataset.compareMode !== 'price';
        const units = [];
        let compare = 0;
        for (let i = 0; i < qty; i++) {
          const v = this.variants.find((x) => String(x.id) === String(this.selectedVariants[key][i]));
          if (!v) return;
          units.push(v.price);
          compare += useCompareAt && v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price;
        }
        const total = units.reduce((a, b) => a + b, 0);
        let price;
        if (free > 0) {
          const freeValue = units
            .slice()
            .sort((a, b) => a - b)
            .slice(0, free)
            .reduce((a, b) => a + b, 0);
          price = Math.round(total - freeValue - fixed);
        } else {
          price = Math.round(total * left - fixed);
        }
        const saved = compare - price;
        this.offerState[key] = {
          price,
          compare,
          priceHtml: formatMoney(price, this.moneyFormat),
          compareHtml: formatMoney(compare, this.moneyFormat),
          savePct: compare > price ? Math.round((saved * 100) / compare) : 0,
        };
        const tokens = {
          '[quantity]': qty,
          '[price]': formatMoney(price, this.moneyFormat, true),
          '[compare_price]': formatMoney(compare, this.moneyFormat, true),
          '[amount_saved]': formatMoney(saved, this.moneyFormat, true),
          '[amount_saved_rounded]': formatMoney(Math.round(saved / 100) * 100, this.moneyFormat, true),
          '[price_each]': formatMoney(Math.round(price / qty), this.moneyFormat, true),
          '[compare_price_each]': formatMoney(Math.round(compare / qty), this.moneyFormat, true),
          '[percentage_saved]': this.offerState[key].savePct,
        };
        label.querySelectorAll('.variant-price-update').forEach((el) => {
          let text = el.dataset.text || '';
          Object.keys(tokens).forEach((t) => (text = text.split(t).join(tokens[t])));
          el.innerHTML = text;
        });
        const cmp = label.querySelector('.quantity-break__compare-price');
        if (cmp) cmp.classList.toggle('hidden', !(compare > price));
      }

      getState() {
        const s = this.offerState[this.selectedInput] || {};
        return {
          section: this.section,
          qty: this.selectedQuantity,
          priceHtml: s.priceHtml,
          compareHtml: s.compareHtml,
          onSale: s.compare > s.price,
          savePct: s.savePct || 0,
        };
      }

      emit() {
        this.dispatchEvent(new CustomEvent('mss-mug:change', { bubbles: true, detail: this.getState() }));
      }

      // Shrine ProductForm bundle branch: untouched -> [form id x tier qty]; touched -> the tier's units merged by id.
      getItems() {
        const merged = new Map();
        const ids = this.touched ? this.selectedVariants[this.selectedInput] || [] : [];
        if (ids.length) {
          ids.forEach((id) => merged.set(String(id), (merged.get(String(id)) || 0) + 1));
        } else {
          const input = formIdInput(this.section);
          const id = (input && input.value) || this.dataset.fallbackVariant;
          merged.set(String(id), this.selectedQuantity);
        }
        return Array.from(merged, ([id, quantity]) => ({ id: Number(id), quantity }));
      }

      // .co re-renders the bundle when the desktop sticky character dropdown changes the variant: every row defaults
      // to that character (thumbnail swapped, select back on the placeholder), the checked tier is kept, and the
      // bundle is untouched again, so the next add is the form id x tier qty.
      resetToVariant(id) {
        if (!this.labels || !this.labels.length) return;
        const variant = (this.variants || []).find((v) => String(v.id) === String(id));
        if (!variant) return;
        const vid = String(variant.id);
        const image = variant.featured_image;
        const src = image && (image.src || image);
        (this.rows || []).forEach((row) => {
          row.dataset.selectedId = vid;
          row.querySelectorAll('select').forEach((select) => {
            const placeholder = select.querySelector('option[value=""]');
            if (placeholder) placeholder.selected = true;
            else select.selectedIndex = -1;
          });
          const img = row.querySelector('.quantity-break__selector-item__image img');
          if (img && src) {
            img.src = `${src}${String(src).indexOf('?') > -1 ? '&' : '?'}width=600`;
            img.alt = (image && image.alt) || '';
          }
        });
        Object.keys(this.selectedVariants).forEach((key) => {
          this.selectedVariants[key] = this.selectedVariants[key].map(() => vid);
        });
        this.touched = false;
        if (this.updatePrices && this.priceVaries) this.labels.forEach((label) => this.recompute(label, label.dataset.input));
        this.emit();
      }
    }
    customElements.define('mss-mug-bundle', MssMugBundle);
  }

  /* ---------------- <mss-mug-dates> ---------------- */

  if (!customElements.get('mss-mug-dates')) {
    class MssMugDates extends HTMLElement {
      connectedCallback() {
        this.render();
      }

      format(date) {
        const months = String(this.dataset.months || 'Jan, Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec')
          .split(',')
          .map((m) => m.trim());
        const day = date.getDate();
        const d100 = day % 100;
        const d10 = day % 10;
        let suffix = 'th';
        if (d100 < 11 || d100 > 13) {
          if (d10 === 1) suffix = 'st';
          else if (d10 === 2) suffix = 'nd';
          else if (d10 === 3) suffix = 'rd';
        }
        const month = months[date.getMonth()] || '';
        return this.dataset.dateFormat === 'dd_mm' ? `${day}${suffix} ${month}` : `${month} ${day}${suffix}`;
      }

      render() {
        const now = new Date();
        const addDays = (n) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + n);
        this.querySelectorAll('[data-text]').forEach((p) => {
          const start = this.format(addDays(parseInt(p.dataset.minDays, 10) || 0));
          const end = this.format(addDays(parseInt(p.dataset.maxDays, 10) || 0));
          p.innerHTML = String(p.dataset.text).split('[start_date]').join(start).split('[end_date]').join(end);
        });
      }
    }
    customElements.define('mss-mug-dates', MssMugDates);
  }

  /* ---------------- <mss-mug-sticky> ---------------- */

  if (!customElements.get('mss-mug-sticky')) {
    class MssMugSticky extends HTMLElement {
      connectedCallback() {
        this.section = this.dataset.section;
        this.anchorEl = null;
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.select = this.querySelector('[data-sticky-select]');
        this.image = this.querySelector('[data-sticky-image]');
        // a re-connected bar (theme editor) keeps its classes, so start from what it shows
        this.shown = this.classList.contains('is-shown');
        this.forced = false;

        this.onScroll = () => {
          if (this.frame) return;
          this.frame = window.requestAnimationFrame(() => {
            this.frame = null;
            this.check();
          });
        };
        this.onChange = (event) => {
          if (event.detail && event.detail.section === this.section) this.updatePrice(event.detail);
        };
        this.onSelect = () => this.selectVariant();
        // Theme editor: selecting the sticky block shows the bar so it can be edited.
        this.onEditorSelect = (event) => {
          if (event.target !== this) return;
          this.forced = event.type === 'shopify:block:select';
          this.check();
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        document.addEventListener('mss-mug:change', this.onChange);
        document.addEventListener('shopify:block:select', this.onEditorSelect);
        document.addEventListener('shopify:block:deselect', this.onEditorSelect);
        if (this.select) this.select.addEventListener('change', this.onSelect);

        this.classObserver = new MutationObserver(() => this.syncPadding());
        this.classObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

        const bundle = document.getElementById(`quantity-breaks-${this.section}`);
        if (bundle && typeof bundle.getState === 'function' && bundle.offerState) this.updatePrice(bundle.getState());
        this.check();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        document.removeEventListener('mss-mug:change', this.onChange);
        document.removeEventListener('shopify:block:select', this.onEditorSelect);
        document.removeEventListener('shopify:block:deselect', this.onEditorSelect);
        if (this.select) this.select.removeEventListener('change', this.onSelect);
        if (this.classObserver) this.classObserver.disconnect();
        if (this.frame) window.cancelAnimationFrame(this.frame);
        document.documentElement.classList.remove('mss-mug-satc-on');
      }

      // What the bar watches: the main Add to cart button (.co), or, when the Add to cart block is removed,
      // the bundle builder, else the whole buy box, so the bar (then the only add-to-cart control) still shows.
      anchor() {
        if (this.anchorEl && this.anchorEl.isConnected) return this.anchorEl;
        this.anchorEl =
          [`ProductSubmitButton-${this.section}`, `quantity-breaks-${this.section}`, `ProductInfo-${this.section}`]
            .map((id) => document.getElementById(id))
            .find(Boolean) || null;
        return this.anchorEl;
      }

      // .co StickyAtc.checkScroll(): shown once the anchor's bottom edge is above the screen.
      check() {
        let show = false;
        const anchor = this.forced ? null : this.anchor();
        if (this.forced) show = true;
        else if (anchor) {
          const top = anchor.getBoundingClientRect().top + window.scrollY;
          show = window.scrollY > top + anchor.offsetHeight;
        }
        if (show !== this.shown) {
          this.shown = show;
          this.classList.toggle('is-shown', show);
          this.setAttribute('aria-hidden', show ? 'false' : 'true');
          if (show) this.removeAttribute('inert');
          else this.setAttribute('inert', '');
        }
        this.syncPadding();
      }

      // Room at the bottom of the page while the bar shows (the .co footer spacer), unless the pick tray owns it.
      syncPadding() {
        const html = document.documentElement;
        const on = this.shown && !html.classList.contains('mss-tray-on');
        if (on) html.style.setProperty('--mss-mug-satc-h', `${this.getBoundingClientRect().height}px`);
        if (html.classList.contains('mss-mug-satc-on') !== on) html.classList.toggle('mss-mug-satc-on', on);
      }

      updatePrice(state) {
        if (!this.priceEl || !state || state.priceHtml == null) return;
        const set = (selector, html) => {
          const el = this.priceEl.querySelector(selector);
          if (el && el.innerHTML !== html) el.innerHTML = html;
        };
        set('[data-price-regular]', state.priceHtml);
        set('[data-price-sale]', state.priceHtml);
        set('[data-price-compare]', state.compareHtml || '');
        const pct = this.priceEl.querySelector('[data-price-pct]');
        if (pct) pct.textContent = String(state.savePct || 0);
        this.priceEl.classList.toggle('is-on-sale', !!state.onSale);
      }

      // .co secondary-variant-select: sets the form's variant id, slides the gallery, swaps the bar image,
      // replaces ?variant= in the URL, and re-renders the bundle so every row defaults to that character
      // (rows back on the placeholder, tier kept, bundle untouched). The checked tier's price is unchanged.
      selectVariant() {
        const option = this.select.selectedOptions && this.select.selectedOptions[0];
        if (!option) return;
        const input = formIdInput(this.section);
        if (input) input.value = option.value;
        const bundle = document.getElementById(`quantity-breaks-${this.section}`);
        if (bundle && typeof bundle.resetToVariant === 'function') bundle.resetToVariant(option.value);
        if (option.dataset.mediaId) slideGallery(this.section, option.dataset.mediaId);
        if (this.image && option.dataset.image) this.image.src = option.dataset.image;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (e) {
          /* ignore */
        }
      }
    }
    customElements.define('mss-mug-sticky', MssMugSticky);
  }
})();
